import { ColorOptions } from "@/src/types";
import { StyledMainSection } from "@/styles/global";
import { DisplayHeadingS } from "@/styles/typography";
import styled from "styled-components";

type TitleProps = {
  $color: ColorOptions;
};

type WrapperProps = {
  $backgroundColor?: ColorOptions;
  $backgroundType?: "card" | "fill";
};

type TableProps = {
  $backgroundColor?: ColorOptions;
  $backgroundType?: "card" | "fill";
  $pink?: boolean;
};

type ContainerProps = {
  $backgroundType: "card" | "fill";
  $pink?: boolean;
};

type TableRowProps = {
  $color: ColorOptions;
  $pink?: boolean;
  $backgroundType: "card" | "fill";
};

type TableHeaderProps = {
  $icons?: boolean;
};

type TableCellProps = {
  $numberOfColumns?: number;
  $color?: ColorOptions;
  $icons?: boolean;
  $pink?: boolean;
};

type RowHeaderProps = {
  $numberOfColumns: number;
  $icons: boolean;
};

const Section = styled(StyledMainSection)``;

const Title = styled(DisplayHeadingS)<TitleProps>`
  color: ${({ theme, $color }) => theme.colors[$color]};
  text-align: center;
  margin-bottom: 40px;
  margin-top: -20px;
`;

const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  padding: ${({ $backgroundType }) =>
    $backgroundType === "fill" ? "40px 0" : ""};
  background-color: ${({ theme, $backgroundColor, $backgroundType }) =>
    $backgroundColor === "pink"
      ? theme.colors[$backgroundColor]
      : $backgroundType === "card"
        ? "transparent"
        : $backgroundColor
          ? theme.colors[$backgroundColor]
          : "transparent"};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0;
    padding: ${({ $backgroundColor }) =>
      $backgroundColor === "pink" && "24px 0"};
  }
`;

const Table = styled.table<TableProps>`
  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor ? theme.colors[$backgroundColor] : "transparent"};
  border-radius: 21px;
  width: 100%;
  border-collapse: ${({ $pink }) => ($pink ? "separate" : "collapse")};
  border-spacing: ${({ $pink }) => $pink && "24px 16px"};
  table-layout: fixed;
  padding: ${({ $backgroundColor, $backgroundType, $pink }) =>
    $pink
      ? "20px 0"
      : $backgroundColor === "transparent"
        ? "40px 0"
        : $backgroundType === "fill"
          ? "40px 0"
          : "40px 48px"};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
    padding: 0;
    border-spacing: ${({ $pink }) => $pink && "0px 12px"};
  }
`;

const Container = styled.div<ContainerProps>`
  padding: ${({ $backgroundType, $pink }) =>
    $pink ? 0 : $backgroundType === "fill" ? "104px 0" : "104px 40px"};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${({ $backgroundType, $pink }) =>
      $pink ? 0 : $backgroundType === "fill" ? "32px 0" : "32px 16px"};
  }
`;

const TableHead = styled.thead`
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const TableBody = styled.tbody`
  width: 100%;
`;

const TableRow = styled.tr<TableRowProps>`
  width: 100%;
  border-bottom: ${({ theme, $color, $pink }) =>
    !$pink && `1px solid ${theme.colors[$color]}4D`};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    border: none;
  }
`;

const TableHeader = styled.th<TableHeaderProps>`
  vertical-align: top;
  padding: 24px 12px;

  * {
    text-align: ${({ $icons }) => ($icons ? "center" : "left")};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const TableCell = styled.td<TableCellProps>`
  vertical-align: ${({ $icons }) => ($icons ? "middle" : "top")};
  word-wrap: break-word;
  padding: 24px 8px;
  padding: ${({ $pink }) => $pink && "16px"};
  vertical-align: ${({ $pink }) => $pink && "middle"};
  width: ${({ $numberOfColumns }) =>
    `calc((100vw - 40px) / ${$numberOfColumns})`};

  border-right: none;
  background-color: ${({ $pink, theme }) => $pink && theme.colors.creme};
  border-radius: ${({ $pink }) => $pink && "24px"};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
    width: 100%;
    padding: ${({ $pink }) => ($pink ? "16px" : 0)};
    border: none;
    margin-bottom: ${({ $pink }) => $pink && "8px"};

    h6 {
      text-align: ${({ $pink }) => $pink && "center"};
      margin-top: ${({ $pink }) => $pink && "0"};
    }
  }

  * {
    color: ${({ $pink, theme }) => $pink && theme.colors.black};
  }
`;

const RowHeader = styled.th<RowHeaderProps>`
  padding: 24px;
  text-align: left;
  vertical-align: top;
  min-width: ${({ $numberOfColumns }) =>
    $numberOfColumns < 3 ? "240px" : "140px"};
  width: 500px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
    width: unset;
    padding: 8px 0;
    text-align: left;
  }
`;

export default {
  Section,
  Title,
  Wrapper,
  Table,
  Container,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  RowHeader,
};
